/** Linework icons for the V2 chrome, keyed by surface id (+ 'more').
 *  Ported from V1's RailIcon set — neutral strokes that suit the FUI. */
export function SurfaceIcon({ id }: { id: string }) {
  const common = { fill: 'none' as const, stroke: 'currentColor', strokeWidth: 1.8, 'aria-hidden': true as const }
  switch (id) {
    case 'design':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M3 21l3.6-.8L19 6.8a2 2 0 0 0-2.8-2.8L2.8 17.4 3 21z" />
          <path d="M14.5 6.5l3 3" />
        </svg>
      )
    case 'home':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M3 12L12 3l9 9M5 10v10h14V10" />
        </svg>
      )
    case 'tovos':
      // Constellation: central node + orbiting dots joined by faint spokes.
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M12 12l6-4M12 12l-5-3M12 12l3 6M12 12l-4 4" opacity="0.5" />
          <circle cx="12" cy="12" r="2.4" />
          <circle cx="18" cy="8" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="7" cy="9" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="15" cy="18" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="8" cy="16" r="1.4" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'hoxton':
      // Theatre mask — echoes Hoxton's 🎭 identity (planner/PA persona).
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M5 5.5h14V11a7 7 0 0 1-14 0V5.5z" />
          <circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none" />
          <circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none" />
          <path d="M9 14c1 1 4 1 5 0" />
        </svg>
      )
    case 'projects':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      )
    case 'inbox':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M22 12h-6l-2 3h-4l-2-3H2" />
          <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
        </svg>
      )
    case 'mail':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 6l-10 7L2 6" />
        </svg>
      )
    case 'status':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      )
    case 'hermes':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <circle cx="9" cy="9" r="0.5" fill="currentColor" />
          <circle cx="15" cy="9" r="0.5" fill="currentColor" />
        </svg>
      )
    case 'collab':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="9" cy="12" r="6" />
          <circle cx="15" cy="12" r="6" />
        </svg>
      )
    case 'plans':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M9 4h6a1 1 0 011 1v1a1 1 0 01-1 1H9a1 1 0 01-1-1V5a1 1 0 011-1z" />
          <path d="M6 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-1" />
          <path d="M8 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'workshop':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
        </svg>
      )
    case 'preview':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="2" y="4" width="20" height="14" rx="1" />
          <path d="M8 22h8M12 18v4" strokeLinecap="round" />
        </svg>
      )
    case 'audits':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
          <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'agents':
      // Grid of terminal nodes — a fleet of agent panes.
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="3" y="4" width="8" height="7" rx="1" />
          <rect x="13" y="4" width="8" height="7" rx="1" />
          <rect x="3" y="13" width="8" height="7" rx="1" />
          <rect x="13" y="13" width="8" height="7" rx="1" />
          <path d="M5 6.5l1.5 1L5 8.5M5 15.5l1.5 1L5 17.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'office':
      // Floorplan: a room outline with desk blocks — the spatial crew view.
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <rect x="6" y="6" width="4" height="3" rx="0.6" />
          <rect x="14" y="6" width="4" height="3" rx="0.6" />
          <rect x="6" y="15" width="4" height="3" rx="0.6" />
          <rect x="14" y="15" width="4" height="3" rx="0.6" />
        </svg>
      )
    case 'pdfcn':
      // Document with a folded corner + text lines — the PDF workshop.
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M6 2h8l5 5v15H6z" strokeLinejoin="round" />
          <path d="M14 2v5h5" strokeLinejoin="round" />
          <path d="M9 12h6M9 16h6" strokeLinecap="round" />
        </svg>
      )
    case 'more':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="5" cy="12" r="1.5" fill="currentColor" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <circle cx="19" cy="12" r="1.5" fill="currentColor" />
        </svg>
      )
    default:
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      )
  }
}
